import { useCallback, useEffect, useRef, useState } from 'react';

import { ColorModeValue, useUserColorMode } from '@bonusly/design-system';

import { getActiveSeasonConfig } from '../components/seasonal/seasonal-config';
import { useAppearancePreferences, useUpdateAppearancePreferences } from '../queries/identity';

/**
 * Check if the seasonal theme is enabled by reading localStorage directly.
 * This avoids depending on React context (which has timing issues) and
 * gives a reliable synchronous answer.
 */
const isSeasonalThemeActiveSync = (): boolean => {
	try {
		const stored = localStorage.getItem(getActiveSeasonConfig().storageKey);
		return stored ? JSON.parse(stored) === true : false;
	} catch {
		return false;
	}
};

/** localStorage key used by useUserColorMode */
const USER_PREFERENCE_STORAGE_KEY = 'bonusly-color-mode-preference';

/**
 * Module-level flag to ensure sync only happens once across all hook instances.
 * This prevents duplicate API calls when multiple components use this hook simultaneously
 * (e.g., AppearancePreferencesSyncProvider and ThemeMenuItem).
 */
let globalSyncInitiated = false;

/**
 * Reset the global sync flag. Useful for testing or when user logs out.
 */
export const resetGlobalSyncState = () => {
	globalSyncInitiated = false;
};

/**
 * Check if localStorage has an explicit color mode preference
 * Returns the preference if set, null if not set
 */
const getLocalStoragePreference = (): ColorModeValue | null => {
	if (typeof window === 'undefined') return null;

	const stored = localStorage.getItem(USER_PREFERENCE_STORAGE_KEY);
	if (stored === 'system' || stored === 'light' || stored === 'dark') {
		return stored;
	}

	// Also check Chakra's key for migration (same logic as base hook)
	const chakraStored = localStorage.getItem('chakra-ui-color-mode');
	if (chakraStored === 'dark') {
		return 'dark';
	}

	return null;
};

/**
 * Hook that extends useUserColorMode with backend synchronization.
 *
 * Bidirectional sync logic on mount:
 * - If localStorage exists but backend is null → write localStorage to backend (preserves existing users)
 * - If backend exists but localStorage is null → write backend to localStorage (restores after logout)
 * - If neither exists → default to light, write to both
 * - If both exist and differ → backend wins (source of truth for cross-device sync)
 *
 * On change:
 * - Persists the new preference to both localStorage and backend
 *
 * Use this hook in authenticated contexts where you want user preferences
 * to persist across sessions and devices.
 */
export const useUserColorModeWithSync = () => {
	const baseHook = useUserColorMode();
	const { data: backendPreferences, isSuccess: hasLoadedBackend, isLoading: isLoadingBackend } = useAppearancePreferences();
	const { mutate: updateBackend } = useUpdateAppearancePreferences();

	// Track if we've done the initial sync (reactive state for consumers)
	const [isSynced, setIsSynced] = useState(false);

	// Track if we're currently applying a sync (to avoid circular updates)
	const isSyncingRef = useRef(false);

	// Bidirectional sync on mount
	useEffect(() => {
		// When the seasonal theme is active, it owns color mode via localStorage.
		// Skip the sync to prevent the backend preference from overwriting it.
		if (isSeasonalThemeActiveSync()) {
			if (!isSynced) setIsSynced(true);
			return;
		}

		// Wait for backend to successfully load
		// Using hasLoadedBackend (isSuccess) instead of !isLoadingBackend to ensure
		// we don't run sync logic when the query fails (which would also set isLoading=false)
		if (!hasLoadedBackend) {
			return;
		}

		// Check both local and global sync flags to prevent duplicate syncs
		// Global flag prevents multiple hook instances from syncing simultaneously
		if (isSynced || globalSyncInitiated) {
			// Still update local state so this instance knows sync is complete
			if (!isSynced) {
				setIsSynced(true);
			}
			return;
		}

		// Mark as synced immediately to prevent re-runs (both local and global)
		globalSyncInitiated = true;
		setIsSynced(true);

		const localPref = getLocalStoragePreference();
		// backendPreferences is null if no record exists, or has colorMode if set
		const backendPref = backendPreferences?.colorMode ?? null;

		// Case 1: localStorage exists, backend is null → preserve existing user's preference
		if (localPref !== null && backendPref === null) {
			// Write localStorage preference to backend
			updateBackend(
				{ colorMode: localPref },
				{
					onError: (error) => {
						console.warn('Failed to sync localStorage preference to backend:', error);
					},
				}
			);
			// No need to update local state - it already has the right value
			return;
		}

		// Case 2: Backend exists, localStorage is null → restore from backend (after logout/new device)
		if (backendPref !== null && localPref === null) {
			isSyncingRef.current = true;
			baseHook.setColorMode(backendPref as ColorModeValue);
			setTimeout(() => {
				isSyncingRef.current = false;
			}, 0);
			return;
		}

		// Case 3: Neither exists → default to light
		if (backendPref === null && localPref === null) {
			isSyncingRef.current = true;
			baseHook.setColorMode('light');
			updateBackend(
				{ colorMode: 'light' },
				{
					onError: (error) => {
						console.warn('Failed to set default preference in backend:', error);
					},
				}
			);
			setTimeout(() => {
				isSyncingRef.current = false;
			}, 0);
			return;
		}

		// Case 4: Both exist - backend is source of truth for cross-device sync
		if (backendPref !== null && localPref !== null && backendPref !== localPref) {
			isSyncingRef.current = true;
			baseHook.setColorMode(backendPref as ColorModeValue);
			setTimeout(() => {
				isSyncingRef.current = false;
			}, 0);
			return;
		}

		// Case 5: Both exist and match - nothing to do
	}, [hasLoadedBackend, isSynced, backendPreferences, baseHook, updateBackend]);

	// Helper to persist to backend
	const persistToBackend = useCallback(
		(mode: ColorModeValue) => {
			// Don't persist to backend if we're syncing (prevents circular updates)
			if (isSyncingRef.current) {
				return;
			}

			// Persist to backend (fire and forget - we don't want to block the UI)
			updateBackend(
				{ colorMode: mode },
				{
					onError: (error) => {
						console.warn('Failed to persist color mode preference to backend:', error);
					},
				}
			);
		},
		[updateBackend]
	);

	// Wrap setColorMode to also persist to backend
	const setColorMode = useCallback(
		(mode: ColorModeValue) => {
			// Apply locally first for immediate feedback
			baseHook.setColorMode(mode);
			persistToBackend(mode);
		},
		[baseHook, persistToBackend]
	);

	// Wrap toggleColorMode to also persist to backend
	const toggleColorMode = useCallback(() => {
		// Toggle locally first
		baseHook.toggleColorMode();

		// Determine the new mode after toggle and persist it
		// The base hook toggles between light/dark based on current colorMode
		const newMode = baseHook.colorMode === 'light' ? 'dark' : 'light';
		persistToBackend(newMode);
	}, [baseHook, persistToBackend]);

	return {
		...baseHook,
		setColorMode,
		toggleColorMode,
		/** Whether the initial sync has completed (reactive) */
		isSynced,
		/** Whether we're still loading backend preferences */
		isLoading: isLoadingBackend,
	};
};
